import React, { createContext, useContext, useEffect, useState } from 'react';
import { Auth, Amplify, API } from 'aws-amplify';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../config';
import useApi from '../projects/shared/hooks/useApi';

const ProjectContext = createContext();

export const ProjectProvider = ({ children }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [allowedPages, setAllowedPages] = useState(null);
  const [allowedProjects, setAllowedProjects] = useState(null);
  const { apiGet } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  // Initialize state from localStorage on mount
  useEffect(() => {
    const storedPages = localStorage.getItem('allowedPages');
    const storedProjects = localStorage.getItem('allowedProjects');
    
    if (storedPages) {
      setAllowedPages(JSON.parse(storedPages));
    }
    if (storedProjects) {
      setAllowedProjects(JSON.parse(storedProjects));
    }

    // Check authentication status on mount
    checkAuthAndFetchRoles();
  }, []);

  const getAllPages = (projects) => {
    let allPages = [];
    projects.forEach((project) => {
      Object.values(project.roles).forEach((role) => {
        if (role.pages) {
          allPages.push(...role.pages);
        }
      });
    });
    return [...new Set(allPages)];
  };

  const getUserProjects = async (data) => {
    return data?.projects?.map(project => project.name) || [];
  }

  const getUserRoles = async (data) => {
    const all_roles_keys = data?.projects?.map(project => Object.keys(project.roles)) || [];
    const all_roles_keys_flat = all_roles_keys.flat();
    return all_roles_keys_flat.map(role => role.toLowerCase());
  }




  const fetchUserRoles = async () => {
    try {
      const getDataRoute = `/get-user-info`;
      const data = await apiGet("UX_HTTP_API", getDataRoute);
      if (data.projects) {
        const projectNames = await getUserProjects(data);
        const roles = await getUserRoles(data);
        const pages = getAllPages(data.projects);
        // Update both state and localStorage
        setAllowedProjects(projectNames);
        setAllowedPages(pages);
        
        localStorage.setItem('allowedProjects', JSON.stringify(projectNames));
        localStorage.setItem('all_roles', JSON.stringify(roles));
        localStorage.setItem('allowedPages', JSON.stringify(pages));
      }
    } catch (error) {
      console.error('Project Context Error fetching user roles:', error);
    }
  };

  const checkAuthAndFetchRoles = async () => {
    try {
      const session = await Auth.currentSession();
      if (session) {
        await fetchUserRoles();
      }
    } catch (error) {
      if (error !== 'No current user') {
        console.error('Error checking session:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      // Clear both state and localStorage
      setAllowedPages(null);
      setAllowedProjects(null);
      setCurrentProject(null);
      
      localStorage.removeItem('selectedProject');
      localStorage.removeItem('allowedPages');
      localStorage.removeItem('allowedProjects');
      localStorage.removeItem('all_roles');
      
      navigate('/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        userRole,
        allowedPages,
        allowedProjects,
        handleLogout,
        refreshPermissions: fetchUserRoles
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};